// hooks/useGeoSearch.ts
import { useMemo, useState, useEffect } from 'react';
import type { GeoCollection, GeoFeature, SearchResult } from '../type';

/**
 * Normalise une chaîne :
 * - Corrige les accents cassés (Ã© → é)
 * - Minuscule
 * - Enlève les accents
 * - Trim + collapse des espaces
 */
export function normalize(str: string): string {
  if (!str) return '';
  const normalized = str
    // Corrige l'encodage Latin-1 mal décodé (UTF-8 → Latin-1)
    .replace(/Ã©/g, 'é')
    .replace(/Ã¨/g, 'è')
    .replace(/Ã /g, 'à')
    .replace(/Ã¢/g, 'â')
    .replace(/Ã®/g, 'î')
    .replace(/Ã´/g, 'ô')
    .replace(/Ã»/g, 'û')
    .replace(/Ã§/g, 'ç')
    .replace(/Ã«/g, 'ë')
    .replace(/Ã¯/g, 'ï')
    .replace(/Ã¼/g, 'ü')
    .replace(/Ã¶/g, 'ö')
    .replace(/Ã¤/g, 'ä')
    .replace(/Ã‰/g, 'É')
    .replace(/Ã€/g, 'À')
    // Puis normalisation Unicode standard
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
    .replace(/\s+/g, ' ');
  return normalized;
}

/**
 * Calcule un centroïde approximatif (moyenne des points de la 1ère ring).
 * Suffisant pour centrer une carte.
 */
function computeCentroid(feature: GeoFeature): [number, number] {
  const coords =
    feature.geometry.type === 'Polygon'
      ? feature.geometry.coordinates[0]
      : feature.geometry.coordinates[0][0];

  let sumLng = 0;
  let sumLat = 0;
  const n = coords.length;

  for (const [lng, lat] of coords as number[][]) {
    sumLng += lng;
    sumLat += lat;
  }

  return [sumLng / n, sumLat / n];
}

/**
 * Convertit une feature GeoJSON en SearchResult optimisé pour la recherche.
 */
function featureToSearchResult(feature: GeoFeature): SearchResult {
  const p = feature.properties;
  return {
    id: p.shapeID,
    name: normalize(p.shapeName),
    type: p.shapeType,
    group: p.shapeGroup,
    iso: p.shapeISO,
    centroid: computeCentroid(feature),
    feature,
  };
}

interface UseGeoSearchOptions {
  /** Limite le nombre de résultats retournés (défaut: 20) */
  limit?: number;
  /** Filtre par type(s) ADM. Ex: ['ADM3'] pour les communes uniquement */
  types?: string[];
  /** Délai de debounce en ms (défaut: 200) */
  debounceMs?: number;
}

export function useGeoSearch(
  geoData: GeoCollection | null,
  query: string,
  options: UseGeoSearchOptions = {}
) {
  const { limit = 20, types, debounceMs = 200 } = options;

  // Debounce de la query pour éviter de filtrer à chaque frappe
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), debounceMs);
    return () => clearTimeout(timer);
  }, [query, debounceMs]);

  // Pré-indexation : on transforme toutes les features une seule fois
  const indexedResults = useMemo<SearchResult[]>(() => {
    if (!geoData?.features) return [];

    return geoData.features
      .filter((f) => !types || types.includes(f.properties.shapeType))
      .map((f) => ({
        ...featureToSearchResult(f),
        // Champ de recherche interne (non exposé)
        _search: normalize(f.properties.shapeName),
      })) as (SearchResult & { _search: string })[];
  }, [geoData, types]);

  // Filtrage par query
  const results = useMemo<SearchResult[]>(() => {
    const q = normalize(debouncedQuery);
    // const q = debouncedQuery;
    if (!q) return indexedResults.slice(0, limit);

    return indexedResults
      .filter((r) => (r as any)._search.includes(q))
      .sort((a, b) => {
        // Bonus : les résultats qui commencent par la query arrivent en premier
        const aStarts = (a as any)._search.startsWith(q) ? 0 : 1;
        const bStarts = (b as any)._search.startsWith(q) ? 0 : 1;
        if (aStarts !== bStarts) return aStarts - bStarts;
        return a.name.localeCompare(b.name);
      })
      .slice(0, limit);
  }, [indexedResults, debouncedQuery, limit]);

  return {
    results,
    totalCount: indexedResults.length,
    isSearching: debouncedQuery !== query,
  };
}